// Libraries
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Divider } from '@mui/material';

// Layout & Components
import Layout from 'layouts/purchases/pages/singleLead/Layout.js';
import MDBox from 'components/MDBox';
import Grid from '@mui/material/Grid';
import MainPage from 'layouts/purchases/pages/singleLead/Sections/Main/index.js';
import AaroLoadingScreen from 'components/Loading';
import Chat from 'components/General/Chat';
import FileView from 'components/General/File';
import TaskListModalView from 'components/General/Task/Views/TaskList/ModalView';
import MDButton from 'components/MDButton';

// Hooks & Services
import useDialogHook from 'hooks/useDialogHook';
import { getPurchase } from 'services/api/purchases';
import { config } from 'Constants';
// import FileView from 'components/General/File';
import AddMultiFile from 'components/General/File/MultiFile.js';
// Components
import EditPurchase from 'layouts/purchases/forms/EditPurchase.js';
import EditShipments from 'layouts/purchases/forms/EditShipments.js';
import EditFeedback from 'layouts/purchases/forms/EditFeedback.js';

import { getWooOrders } from 'services/outside/woo/orders.js';
import { getParameters } from 'services/api/general/parameter';
import MDTypography from 'components/MDTypography';

const fetchPurchase = async (purchaseID) => {
    try {
        if (!purchaseID) {
            throw new Error('Purchase ID gerekli');
        }
        const response = await getPurchase(purchaseID);
        if (!response?.data?.result) {
            throw new Error('Satın alma verisi bulunamadı');
        }
        return response.data.result;
    } catch (error) {
        console.error('Purchase fetch hatası:', error);
        throw error;
    }
};

const fetchParameters = async (keys) => {
    try {
        if (!keys) {
            throw new Error('Parameter keys gerekli');
        }
        const queryString = keys
            .split(',')
            .map((key) => `key=${key}`)
            .join('&');
        const response = await getParameters(queryString);
        return response?.data?.result || [];
    } catch (error) {
        console.error('Parameters fetch hatası:', error);
        throw error;
    }
};

function SinglePurchasePage({ incomingID, isModal = false, wooCommerceOrderDetails }) {
    const { open, handleClickOpen, handleClose } = useDialogHook();
    const {
        open: openShipments,
        handleClickOpen: handleClickOpenShipments,
        handleClose: handleCloseShipments,
    } = useDialogHook();
    const {
        open: openFeedback,
        handleClickOpen: handleClickOpenFeedback,
        handleClose: handleCloseFeedback,
    } = useDialogHook();
    const { id } = useParams();
    const [value, setValue] = useState(0);
    const [updateFiles, setUpdateFiles] = useState(0);
    const [wooOrders, setWooOrders] = useState(wooCommerceOrderDetails || null);
    const [retryCount, setRetryCount] = useState(0);

    const purchaseID = incomingID || id;

    const paramQuery = 'api_woo_url,api_woo_cs,api_woo_ck';
    const {
        data,
        isLoading: parametersIsLoading,
        error: parametersError,
        refetch: refetchParameters,
    } = useQuery({
        queryKey: ['fetchParameters', paramQuery],
        queryFn: () => fetchParameters(paramQuery),
        staleTime: 600000,
    });

    const {
        data: purchase,
        isLoading: purchaseIsLoading,
        error: purchaseError,
        refetch,
    } = useQuery({
        queryKey: ['purchase', purchaseID],
        queryFn: () => fetchPurchase(purchaseID),
        staleTime: 600000,
    });

    useEffect(() => {
        const fetchWooOrders = async () => {
            if (!wooCommerceOrderDetails && data && purchase) {
                try {
                    const wooParams = data.reduce((acc, param) => {
                        if (param?.key && param?.value) {
                            acc[param.key] = param.value;
                        }
                        return acc;
                    }, {});

                    // WooCommerce parametrelerinin varlığını kontrol et
                    if (!wooParams.api_woo_ck || !wooParams.api_woo_cs || !wooParams.api_woo_url) {
                        console.warn('WooCommerce API parametreleri eksik');
                        return;
                    }

                    const response = await getWooOrders(
                        wooParams.api_woo_ck,
                        wooParams.api_woo_cs,
                        wooParams.api_woo_url,
                        { search: purchaseID }
                    );

                    if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                        setWooOrders(response.data[0]);
                    } else {
                        console.warn('WooCommerce siparişi bulunamadı');
                    }
                } catch (error) {
                    console.error('WooCommerce siparişleri fetch hatası:', error);
                    // Hata durumunda state'i null olarak bırak
                }
            }
        };

        fetchWooOrders();
    }, [wooCommerceOrderDetails, data, purchase, purchaseID]);

    useEffect(() => {
        if (purchase && !purchase.taskList && retryCount < 3) {
            const timer = setTimeout(() => {
                setRetryCount((prev) => prev + 1);
                refetch();
            }, 1000); // 1 saniye bekleyip tekrar dene

            return () => clearTimeout(timer);
        }
    }, [purchase, retryCount, refetch]);

    if (purchaseIsLoading || parametersIsLoading) return <AaroLoadingScreen />;

    if (parametersError) {
        return (
            <MDBox textAlign="center" py={5}>
                <MDTypography variant="h5" color="error" mb={2}>
                    Parametre Yükleme Hatası
                </MDTypography>
                <MDTypography variant="body2" color="text">
                    {parametersError.message || 'Sistem parametreleri yüklenirken bir hata oluştu'}
                </MDTypography>
            </MDBox>
        );
    }

    if (purchaseError) {
        return (
            <MDBox textAlign="center" py={5}>
                <MDTypography variant="h5" color="error" mb={2}>
                    Sipariş Yükleme Hatası
                </MDTypography>
                <MDTypography variant="body2" color="text">
                    {purchaseError.message || 'Sipariş bilgileri yüklenirken bir hata oluştu'}
                </MDTypography>
            </MDBox>
        );
    }

    if (!purchaseID) {
        return (
            <MDBox textAlign="center" py={5}>
                <MDTypography variant="h5" color="warning" mb={2}>
                    Geçersiz Sipariş
                </MDTypography>
                <MDTypography variant="body2" color="text">
                    Sipariş ID'si bulunamadı
                </MDTypography>
            </MDBox>
        );
    }

    if (!purchase) {
        return (
            <MDBox textAlign="center" py={5}>
                <MDTypography variant="h5" color="warning" mb={2}>
                    Sipariş Bulunamadı
                </MDTypography>
                <MDTypography variant="body2" color="text">
                    Belirtilen sipariş sistemde bulunamadı
                </MDTypography>
            </MDBox>
        );
    }

    // User bilgilerini güvenli şekilde al
    let user = null;
    let role = 'user'; // Default role

    try {
        const userString = localStorage.getItem('user');
        if (userString) {
            user = JSON.parse(userString);
            role = user?.role || 'user';
        }
    } catch (error) {
        console.warn("User bilgileri localStorage'dan alınamadı:", error);
    }

    return (
        <Layout setValue={setValue} isModal={isModal}>
            <MDBox
                mt={1}
                sx={{
                    height: isModal ? 'calc(90vh - 120px)' : 'auto',
                    overflowY: isModal ? 'auto' : 'visible',
                    overflowX: 'hidden',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: '#a8a8a8',
                        },
                    },
                }}
            >
                <Grid container spacing={3}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: value === 2 ? 12 : 9,
                        }}
                    >
                        <MDBox mb={3}>
                            <MainPage
                                purchase={purchase}
                                relatedID={purchaseID}
                                updateFiles={updateFiles}
                                setUpdateFiles={setUpdateFiles}
                                wooCommerceOrderDetails={wooOrders || wooCommerceOrderDetails}
                            />
                        </MDBox>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 3,
                        }}
                    >
                        <MDBox mb={3} mt={4}>
                            <Chat relatedID={purchaseID} source="purchases" />
                        </MDBox>

                        {purchase?.taskList && (
                            <MDBox mb={3} bgColor="white" variant="gradient" borderRadius="lg" shadow="lg" opacity={1}>
                                <TaskListModalView
                                    taskListID={purchase?.taskList}
                                    taskID={null}
                                    url={`${config.url.ENV_URL}/purchases/${purchaseID}`}
                                />
                            </MDBox>
                        )}
                        <MDBox mb={3} display="flex" gap={2}>
                            <MDButton
                                color="info"
                                variant="gradient"
                                sx={{ width: '100%' }}
                                onClick={handleClickOpenShipments}
                            >
                                KARGO GİRİŞİ
                            </MDButton>
                            <MDButton
                                color="success"
                                variant="gradient"
                                sx={{ width: '100%' }}
                                onClick={handleClickOpenFeedback}
                            >
                                GERİ BİLDİRİM
                            </MDButton>
                        </MDBox>
                        <MDBox
                            bgColor="secondary"
                            variant="gradient"
                            borderRadius="lg"
                            shadow="lg"
                            opacity={1}
                            p={2}
                            mb={3}
                            sx={{
                                background: 'linear-gradient(195deg, #42424a, #191919)',
                                color: 'white',
                            }}
                        >
                            {purchase?.shipments && purchase.shipments.length > 0 && (
                                <MDBox>
                                    <MDTypography variant="caption" mb={1} fontWeight="bold" color="white">
                                        Kargo Bilgileri
                                    </MDTypography>
                                    {purchase.shipments.map((shipment, index) => (
                                        <MDBox key={index} mb={1}>
                                            {shipment.shippingMethod && (
                                                <MDTypography variant="overline" color="white">
                                                    Kargo Firması: {shipment.shippingMethod}
                                                </MDTypography>
                                            )}
                                            <br />
                                            {shipment.trackingNumber && (
                                                <MDTypography variant="overline" color="white">
                                                    Takip No: {shipment.trackingNumber}
                                                </MDTypography>
                                            )}
                                            <br />
                                            {shipment.shippingCost > 0 && (
                                                <MDTypography variant="overline" color="white">
                                                    Kargo Ücreti: {shipment.shippingCost} TL
                                                </MDTypography>
                                            )}
                                            <Divider
                                                sx={{
                                                    my: 2,
                                                    opacity: '0.6',
                                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                                }}
                                            />
                                        </MDBox>
                                    ))}
                                </MDBox>
                            )}

                            {(purchase?.customerRating || purchase?.customerFeedback) && (
                                <MDBox>
                                    <MDTypography variant="caption" mb={1} fontWeight="bold" color="white">
                                        Müşteri Geri Bildirimi
                                    </MDTypography>
                                    <MDBox mb={1}>
                                        {purchase.customerRating && (
                                            <MDTypography variant="overline" color="white">
                                                Değerlendirme: {purchase.customerRating}/5
                                            </MDTypography>
                                        )}
                                        <br />
                                        {purchase.customerFeedback && (
                                            <MDTypography variant="overline" color="white">
                                                Yorum: {purchase.customerFeedback}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </MDBox>
                            )}
                        </MDBox>
                        <MDBox
                            mb={3}
                            bgColor="white"
                            display="grid"
                            alignItems="center"
                            color="white"
                            shadow="md"
                            borderRadius="lg"
                            variant="gradient"
                            sx={{
                                background: 'linear-gradient(195deg, #ffffff, #f8f9fa)',
                                border: '1px solid #e9ecef',
                            }}
                        >
                            <AddMultiFile
                                relatedID={purchaseID}
                                additionalName={`woosiparis-${purchaseID}`}
                                filePath={`purchases/${purchaseID}`}
                                setUpdateFiles={setUpdateFiles}
                                source="purchases"
                            />
                        </MDBox>
                        <MDBox mb={3}>
                            <FileView
                                relatedID={purchaseID}
                                updateFiles={updateFiles}
                                setUpdateFiles={setUpdateFiles}
                            />
                        </MDBox>

                        <MDBox mb={3} display="flex" gap={2}>
                            {role === 'superadmin' && (
                                <MDButton
                                    color="dark"
                                    variant="gradient"
                                    sx={{ width: '100%' }}
                                    onClick={handleClickOpen}
                                >
                                    Düzenle
                                </MDButton>
                            )}
                        </MDBox>

                        {open && <EditPurchase open={open} handleClose={handleClose} initial={purchase} />}
                        {openShipments && (
                            <EditShipments open={openShipments} handleClose={handleCloseShipments} initial={purchase} />
                        )}
                        {openFeedback && (
                            <EditFeedback open={openFeedback} handleClose={handleCloseFeedback} initial={purchase} />
                        )}
                    </Grid>
                </Grid>
            </MDBox>
        </Layout>
    );
}

export default SinglePurchasePage;
